import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Emoji } from '@/components/Emoji';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SpotlightCard } from '@/components/SpotlightCard';
import { VOCAB, VOCAB_BOOKS, VOCAB_BY_ID } from '@/lib/data';
import { useAppStore } from '@/lib/store';
import { daysUntilDue, isDue } from '@/lib/srs';

export function VocabPage(): JSX.Element {
  const srs = useAppStore((s) => s.srs);
  const enrolledBookIds = useAppStore((s) => s.enrolledBookIds);
  const enroll = useAppStore((s) => s.enrollVocabBook);

  const [filter, setFilter] = useState<'all' | 'enrolled' | 'due'>('all');

  const stats = useMemo(() => {
    const cards = Object.values(srs);
    const due = cards.filter((c) => isDue(c)).length;
    const learning = cards.filter((c) => c.reps > 0 && c.reps < 3).length;
    const mastered = cards.filter((c) => c.reps >= 3 && c.lapses === 0).length;
    return { due, learning, mastered, total: cards.length };
  }, [srs]);

  const dueWords = VOCAB.filter((w) => {
    const card = srs[w.id];
    return card !== undefined && isDue(card);
  });

  return (
    <div className="space-y-12">
      <Reveal>
        <div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-white">
            <span className="text-gradient-warm">单词记忆</span> · Vocab Engine
          </h1>
          <p className="mt-3 text-cream-300/90">
            150,000+ 词条 · 艾宾浩斯算法 · AI 场景配图 · 卡牌式记忆
          </p>
        </div>
      </Reveal>

      {/* Stats */}
      <Reveal delay={0.05}>
        <div className="grid gap-4 md:grid-cols-4">
          <StatBlock label="待复习" value={stats.due} tone="ember" emoji="📅" />
          <StatBlock label="学习中" value={stats.learning} tone="honey" emoji="📖" />
          <StatBlock label="已掌握" value={stats.mastered} tone="mint" emoji="✓" />
          <StatBlock label="总单词" value={stats.total} tone="cream" emoji="✨" />
        </div>
      </Reveal>

      {/* Today's review CTA */}
      <Reveal delay={0.1}>
        <SpotlightCard spotlightColor="rgba(20, 184, 166, 0.28)">
          <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-mint-300 font-semibold">
                Today's Review · 今日复习
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold text-white">
                {dueWords.length === 0 ? '🎉 全部复习完了' : `${dueWords.length} 张卡片到期`}
              </h2>
              <p className="mt-2 text-sm text-cream-300/90">
                {dueWords.length === 0
                  ? '休息一下吧 — 明天再回来'
                  : '今天不复习，明天就再来一遍'}
              </p>
            </div>
            {dueWords.length === 0 ? (
              <GlassButton size="lg" disabled>都搞定了 ✓</GlassButton>
            ) : (
              <GlassButton to="/app/vocab/review" size="lg" variant="solid">
                开始复习 →
              </GlassButton>
            )}
          </div>
        </SpotlightCard>
      </Reveal>

      {/* Vocab books */}
      <Reveal>
        <div>
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">单词本</h2>
              <p className="text-sm text-cream-400 mt-1">81 个精选 · 按场景挑一本</p>
            </div>
            <div className="flex gap-1 rounded-full bg-white/[0.04] border border-white/[0.06] p-1 text-xs">
              {[
                ['all', '全部'],
                ['enrolled', '我的'],
                ['due', '有待复习'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k as typeof filter)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    filter === k
                      ? 'bg-white/[0.08] text-white'
                      : 'text-cream-400 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {VOCAB_BOOKS.filter((b) => {
              if (filter === 'enrolled') return enrolledBookIds.includes(b.id);
              if (filter === 'due') {
                return b.wordIds.some((id) => {
                  const c = srs[id];
                  return c !== undefined && isDue(c);
                });
              }
              return true;
            }).map((b) => {
              const enrolled = enrolledBookIds.includes(b.id);
              const due = b.wordIds.filter((id) => {
                const c = srs[id];
                return c !== undefined && isDue(c);
              }).length;
              return (
                <SpotlightCard key={b.id} className="h-full">
                  <div className="flex items-start gap-4">
                    <Emoji char={b.emoji} size={40} />
                    <div className="flex-1">
                      <div className="font-display text-lg font-bold text-white">{b.title}</div>
                      <div className="text-xs text-cream-400 mt-1">{b.wordIds.length} 个单词 · {due} 待复习</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-cream-300/90">{b.description}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {b.wordIds.slice(0, 6).map((id) => {
                      const w = VOCAB_BY_ID[id];
                      if (w === undefined) return null;
                      return (
                        <span key={id} className="text-xs px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-cream-200 font-mono">
                          {w.en}
                        </span>
                      );
                    })}
                    {b.wordIds.length > 6 && (
                      <span className="text-xs px-2 py-0.5 text-cream-400">+{b.wordIds.length - 6}</span>
                    )}
                  </div>
                  <div className="mt-5">
                    {enrolled ? (
                      <GlassButton to="/app/vocab/review" size="sm" variant="solid" className="w-full">
                        进入学习
                      </GlassButton>
                    ) : (
                      <GlassButton
                        onClick={() => enroll(b.id, b.wordIds)}
                        size="sm"
                        className="w-full"
                      >
                        + 加入我的单词本
                      </GlassButton>
                    )}
                  </div>
                </SpotlightCard>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* All words sample */}
      <Reveal>
        <div>
          <h2 className="font-display text-2xl font-bold text-white mb-5">所有单词预览</h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {VOCAB.slice(0, 12).map((w) => {
              const card = srs[w.id];
              const due = card !== undefined && isDue(card);
              return (
                <div key={w.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center gap-3">
                  <Emoji char={w.imageEmoji} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-white font-semibold truncate">{w.en}</div>
                    <div className="text-xs text-cream-400 truncate">{w.zh} · {w.ipa}</div>
                  </div>
                  {card !== undefined && (
                    <div className={`text-xs px-1.5 py-0.5 rounded ${due ? 'bg-ember-500/20 text-ember-300' : 'bg-mint-500/15 text-mint-300'}`}>
                      {due ? '到期' : `${daysUntilDue(card)}d`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function StatBlock({
  label,
  value,
  tone,
  emoji,
}: {
  label: string;
  value: number;
  tone: 'ember' | 'honey' | 'mint' | 'cream';
  emoji: string;
}): JSX.Element {
  const toneClass =
    tone === 'ember'
      ? 'text-ember-300'
      : tone === 'honey'
        ? 'text-honey-300'
        : tone === 'mint'
          ? 'text-mint-300'
          : 'text-cream-200';
  return (
    <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xl">{emoji}</span>
        <span className={`font-display text-3xl font-extrabold ${toneClass}`}>{value}</span>
      </div>
      <div className="mt-3 text-xs text-cream-400">{label}</div>
    </div>
  );
}
